import { useMemo } from 'react';

// Дискретные стопы ползунка. 0 в БД означает «∞» (все разом).
// В UI показываем ∞ на самой правой позиции.
const STOPS: number[] = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 0];
const INFINITY_LABEL = '∞';

interface SessionSizeSliderProps {
  value: number;            // 0 = ∞, иначе одно из значений STOPS
  onChange: (v: number) => void;
  label: string;
  description?: string;
}

function valueToIndex(v: number): number {
  const idx = STOPS.indexOf(v);
  if (idx >= 0) return idx;
  // Ближайший стоп, если в БД значение которого нет в списке (например legacy 20)
  if (v === 0) return STOPS.length - 1;
  let nearest = 0;
  let minDiff = Infinity;
  for (let i = 0; i < STOPS.length - 1; i++) {
    const d = Math.abs(STOPS[i] - v);
    if (d < minDiff) {
      minDiff = d;
      nearest = i;
    }
  }
  return nearest;
}

function formatValue(v: number): string {
  return v === 0 ? INFINITY_LABEL : String(v);
}

function formatUnit(v: number): string {
  if (v === 0) return 'без ограничения';
  if (v === 1) return 'карточка';
  if (v >= 2 && v <= 4) return 'карточки';
  return 'карточек';
}

export function SessionSizeSlider({ value, onChange, label, description }: SessionSizeSliderProps) {
  const index = useMemo(() => valueToIndex(value), [value]);
  const displayValue = STOPS[index];

  return (
    <div style={{ padding: '14px 0', borderBottom: '1px solid var(--theme-border)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, color: 'var(--theme-text)', fontWeight: 500 }}>{label}</div>
          {description && (
            <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 2 }}>{description}</div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{
            fontSize: 24,
            fontWeight: 700,
            color: 'var(--theme-primary)',
            lineHeight: 1,
          }}>{formatValue(displayValue)}</span>
          <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', marginTop: 2 }}>{formatUnit(displayValue)}</div>
        </div>
      </div>

      {/* Слайдер */}
      <div style={{ position: 'relative', padding: '8px 0 4px 0' }}>
        <input
          type="range"
          min={0}
          max={STOPS.length - 1}
          step={1}
          value={index}
          onChange={e => onChange(STOPS[Number(e.target.value)])}
          style={{
            width: '100%',
            height: 28,
            accentColor: 'var(--theme-primary)',
            cursor: 'pointer',
            WebkitAppearance: 'none',
            appearance: 'none',
            background: 'transparent',
            touchAction: 'none',
          }}
        />
        {/* Точки-отметки под треком */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 4px',
          marginTop: -4,
          pointerEvents: 'none',
        }}>
          {STOPS.map((s, i) => (
            <span
              key={i}
              style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                background: i === index ? 'var(--theme-primary)' : 'var(--theme-text-muted)',
                opacity: i === index ? 1 : 0.4,
              }}
            />
          ))}
        </div>
        {/* Подписи крайних точек */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 4,
          fontSize: 11,
          color: 'var(--theme-text-muted)',
        }}>
          <span>5</span>
          <span>{INFINITY_LABEL}</span>
        </div>
      </div>
    </div>
  );
}
